import React from "react";
import { alpha, Box, Button, Card, CardMedia, Fade, Grid, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";

// APIレスポンスに合わせてインターフェースを定義（SoundLibraryと同じ定義を使用）
interface SoundItem {
  uuid: string;
  status: number;
  url: string | null;
  created_at: string;
  image_url: string | null;
  params: {
    type?: string;
    model?: string;
    prompt?: string;
    lyric?: string;
    [key: string]: unknown;
    error_code?: string;
    error_detail?: string;
  };
  is_public: boolean;
}

interface SoundDetailModalProps {
  open: boolean;
  onClose: () => void;
  sounds: SoundItem[];
  currentIndex: number;
  handleNextSound: () => void;
  handlePrevSound: () => void;
  handleOpenDeleteDialog: (uuid: string) => void;
  handleSoundDownload: (uuid: string, prompt: string) => void;
  playingUrl: string;
  isPlaying: boolean;
  handleSelect: (url: string | null) => void;
}

const SoundDetailModal: React.FC<SoundDetailModalProps> = ({
  open,
  onClose,
  sounds,
  currentIndex,
  handleNextSound,
  handlePrevSound,
  handleOpenDeleteDialog,
  handleSoundDownload,
  playingUrl,
  isPlaying,
  handleSelect,
}) => {
  const { t } = useTranslation();
  const currentSound = sounds[currentIndex];

  if (!currentSound) {
    return null;
  }

  // エラー状態の判定（status=2）
  const isError = currentSound.status === 2;
  // 処理中の判定（status=3）
  const isProcessing = currentSound.status === 3;

  // パラメータから必要な情報を抽出
  const prompt = currentSound.params?.prompt || "";
  const model = currentSound.params?.model || "";
  const lyric = currentSound.params?.lyric || "";

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "900px",
          backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
          backdropFilter: "blur(20px)",
          boxShadow: 24,
          p: 3,
          outline: "none",
          borderRadius: 2,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* 閉じるボタン */}
        <IconButton onClick={onClose} aria-label="close" sx={{ position: "absolute", top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>

        <Fade in={true} timeout={300}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%", overflow: "hidden" }}>
            {/* レイアウト：画像左・詳細右 */}
            <Grid container spacing={3}>
              {/* 左側：画像とプレイヤーコントロール */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ display: "flex", justifyContent: "center", position: "relative" }}>
                  {currentSound.image_url ? (
                    <Card sx={{ boxShadow: "none", maxWidth: "100%" }}>
                      <CardMedia
                        component="img"
                        sx={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "60vh",
                          objectFit: "contain",
                          borderRadius: 1,
                        }}
                        image={currentSound.image_url}
                        alt={prompt}
                      />
                    </Card>
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        width: "100%",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 250,
                        background: "linear-gradient(135deg, rgba(100,100,240,0.2) 0%, rgba(0,200,255,0.1) 100%)",
                      }}
                    />
                  )}

                  {/* 画像中央の再生ボタン */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Tooltip title={playingUrl === currentSound.url && isPlaying ? t("common.stop") : t("common.play")}>
                      <IconButton
                        onClick={() => handleSelect(currentSound.url)}
                        disabled={isProcessing || isError}
                        color={playingUrl === currentSound.url && isPlaying ? "primary" : "default"}
                        sx={{
                          backgroundColor: "rgba(0, 0, 0, 0.6)",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                          },
                        }}
                        size="large"
                      >
                        {playingUrl === currentSound.url && isPlaying ? (
                          <PauseIcon fontSize="large" />
                        ) : (
                          <PlayArrowIcon fontSize="large" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>

              {/* 右側：詳細情報 */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    p: 1,
                    overflowY: "auto",
                    maxHeight: "400px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  {prompt && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>{t("juno.sound.params.input.label")}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                        {prompt}
                      </Typography>
                    </Box>
                  )}

                  {lyric && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>{t("juno.sound.params.lyric")}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                        {lyric}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" gutterBottom>
                      <strong>{t("juno.sound.ai.model")}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {model ? t(`juno.sound.ai.${model.toLowerCase()}.title`) : ""}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" gutterBottom>
                      <strong>{t("common.created")}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {new Date(currentSound.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* アクションボタン */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, px: 1, py: 2 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  color="error"
                  onClick={() => handleOpenDeleteDialog(currentSound.uuid)}
                  disabled={isProcessing}
                >
                  {t("juno.sound.soundMenu.delete")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleSoundDownload(currentSound.uuid, prompt)}
                  disabled={isProcessing || isError}
                >
                  {t("juno.sound.soundMenu.download")}
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton onClick={handlePrevSound} disabled={sounds.length <= 1}>
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton onClick={handleNextSound} disabled={sounds.length <= 1}>
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>
    </Modal>
  );
};

export default SoundDetailModal;
